import React, {
  useEffect,
  useRef,
  useState,
} from "react";

import {
  api,
} from "../api";

import type {
  Contact,
} from "../types";

import {
  useAuth,
} from "../context/AuthContext";

import {
  useDrawer,
} from "../context/DrawerContext";

export default function TopBar({
  onMenuClick,
}: {
  onMenuClick?: () => void;
}) {
  const {
    user,
    organization,
    membership,
    locations,
    activeLocation,
    organizations,
    switchingOrganization,
    switchingLocation,
    switchOrganization,
    switchLocation,
    logout,
  } =
    useAuth();

  const {
    open,
  } =
    useDrawer();

  const [
    query,
    setQuery,
  ] =
    useState("");

  const [
    results,
    setResults,
  ] =
    useState<Contact[]>([]);

  const [
    showResults,
    setShowResults,
  ] =
    useState(false);

  const [
    switchError,
    setSwitchError,
  ] =
    useState<string | null>(null);

  const debounceRef =
    useRef<
      ReturnType<typeof setTimeout> |
      null
    >(null);

  useEffect(() => {
    if (debounceRef.current) {
      clearTimeout(
        debounceRef.current
      );
    }

    if (!query.trim()) {
      setResults([]);
      return;
    }

    debounceRef.current =
      setTimeout(
        async () => {
          try {
            const response =
              await api.searchContacts(
                query.trim()
              );

            setResults(
              response
            );
            setShowResults(
              true
            );
          } catch {
            // La búsqueda no interrumpe la operación.
          }
        },
        350
      );

    return () => {
      if (debounceRef.current) {
        clearTimeout(
          debounceRef.current
        );
      }
    };
  }, [
    query,
  ]);

  async function onOrganizationChange(
    value: string
  ) {
    if (!value) {
      return;
    }

    setSwitchError(null);

    try {
      await switchOrganization(
        value
      );
    } catch {
      setSwitchError(
        "No se pudo cambiar de organización."
      );
    }
  }

  async function onLocationChange(
    value: string
  ) {
    setSwitchError(null);

    try {
      await switchLocation(
        value
          ? value
          : null
      );
    } catch {
      setSwitchError(
        "No se pudo cambiar de sede."
      );
    }
  }

  return (
    <div className="top">
      <button
        className="menu-toggle"
        onClick={onMenuClick}
        aria-label="Abrir menú"
      >
        ☰
      </button>

      <div className="search-wrap">
        <input
          className="search"
          placeholder="Buscar nombre, teléfono, email..."
          value={query}
          onChange={(event: any) =>
            setQuery(
              event.target.value
            )
          }
          onFocus={() =>
            setShowResults(true)
          }
          onBlur={() =>
            setTimeout(
              () =>
                setShowResults(false),
              150
            )
          }
        />

        {showResults &&
          results.length > 0 && (
            <div className="search-results">
              {results.map(
                (contact) => (
                  <div
                    key={contact.id}
                    className="search-result-item"
                    onMouseDown={() => {
                      open(
                        contact.id
                      );
                      setShowResults(
                        false
                      );
                      setQuery("");
                    }}
                  >
                    <div className="name">
                      {
                        contact.full_name
                      }
                    </div>

                    <div className="meta">
                      {contact.phone}
                      {contact.email
                        ? " · " +
                          contact.email
                        : ""}
                    </div>
                  </div>
                )
              )}
            </div>
          )}
      </div>

      <div className="user-box">
        {organization && (
          <div className="active-organization">
            <span className="active-organization-label">
              Organización
            </span>

            {organizations.length > 1 ? (
              <select
                className="organization-switcher"
                aria-label="Organización activa"
                data-active-organization-id={
                  organization.id
                }
                value={
                  membership?.id ??
                  ""
                }
                disabled={
                  switchingOrganization ||
                  switchingLocation
                }
                onChange={(event: any) =>
                  onOrganizationChange(
                    event.target.value
                  )
                }
              >
                {organizations.map(
                  (access) => (
                    <option
                      key={
                        access.membership
                          .id
                      }
                      value={
                        access.membership
                          .id
                      }
                    >
                      {
                        access.organization
                          .display_name
                      }
                    </option>
                  )
                )}
              </select>
            ) : (
              <span className="active-organization-name">
                {
                  organization.display_name
                }
              </span>
            )}

            {switchingOrganization && (
              <span className="organization-switching">
                Cambiando…
              </span>
            )}

            {switchError && (
              <span
                className="organization-switch-error"
                role="alert"
              >
                {switchError}
              </span>
            )}
          </div>
        )}

        {organization &&
          locations.length > 0 && (
            <div className="active-location">
              <span className="active-organization-label">
                Sede
              </span>

              {locations.length > 1 ||
              membership?.all_locations ? (
                <select
                  className="organization-switcher"
                  aria-label="Sede activa"
                  data-active-location-id={
                    activeLocation?.id ??
                    ""
                  }
                  value={
                    activeLocation?.id ??
                    ""
                  }
                  disabled={
                    switchingOrganization ||
                    switchingLocation
                  }
                  onChange={(event: any) =>
                    onLocationChange(
                      event.target.value
                    )
                  }
                >
                  {membership?.all_locations &&
                    locations.length > 1 && (
                      <option value="">
                        Todas las sedes
                      </option>
                    )}

                  {locations.map(
                    (location) => (
                      <option
                        key={location.id}
                        value={location.id}
                      >
                        {location.name}
                      </option>
                    )
                  )}
                </select>
              ) : (
                <span className="active-organization-name">
                  {activeLocation?.name ||
                    locations[0]?.name}
                </span>
              )}

              {switchingLocation && (
                <span className="organization-switching">
                  Cambiando…
                </span>
              )}
            </div>
          )}

        <div className="active-user">
          <span className="user-name">
            {user?.full_name}
          </span>

          <span className="role-badge">
            {membership?.role_name ||
              user?.role_code}
          </span>
        </div>

        <button
          className="btn-ghost"
          onClick={logout}
        >
          Salir
        </button>
      </div>
    </div>
  );
}
